import { graphql } from 'gatsby';

import { SEO } from 'components';
import { Features, Hero, Testimonials } from 'components/pages/renters';
import { FAQ } from 'components/rebrand/FAQ';
import { Layout } from 'components/rebrand/Layout';
import Tiers from 'components/rebrand/Tiers';

import { IRentersPageProps } from 'typescriptDefs/pages/renters';

import { afTrackingByPage } from 'helpers/getAfTracking';

const Renters = ({
  data: { page },
  pageContext: { locale, linkCanonical, pageIsMultilingual, pageName, schemaMarkupJson, variant },
  ...props
}: IRentersPageProps) => {
  const heroData = {
    heading: page.heading,
    subheading: page.subheading,
    buttonText: page.buttonText,
    heroImage: page.heroImage,
    heroBenefits: page.heroBenefits,
    tracking: {
      queryParams: afTrackingByPage('RENTERS'),
      trackEventProps: {
        category: pageName,
        label: 'Renters Hero',
        locale,
        location: variant,
      },
    },
  };

  const featuresTracking = {
    queryParams: afTrackingByPage('RENTERS'),
    trackEventProps: {
      category: pageName,
      label: 'Renters Features',
      locale,
      location: variant,
    },
  };

  const testimonialsData = {
    heading: page.testimonialsHeading,
    subheading: page.testimonialsSubheading,
    testimonials: page.testimonials,
  };

  return (
    <Layout location={props.location} pageIsMultilingual={pageIsMultilingual} pageName={pageName} pageVariant={variant}>
      <SEO
        canonical={linkCanonical}
        title={page.metadata.title}
        description={page.metadata.description}
        keywords={page.metadata.keywords}
        imageSrc={page.metadata.image?.asset.gatsbyImageData.images.fallback?.src}
        location={props.location}
        multilingual={pageIsMultilingual}
        structuredData={schemaMarkupJson}
      />
      <Hero data={heroData} />
      <Features features={page.features} featuresTracking={featuresTracking} />
      <Tiers
        className="md:[&_.tiers-heading]:w-[55%]"
        heading={page.plansHeading}
        tiers={page.compareTiers}
        pageName={pageName}
        queryParams={afTrackingByPage('RENTERS')}
      />
      <Testimonials data={testimonialsData} />
      <div className="pb-54 pt-90">
        <FAQ faqItems={page.faq.faqItems} heading={page.faq.heading} subheading={page.faq.subheading} />
      </div>
    </Layout>
  );
};

export default Renters;

export const queryGraphQL = graphql`
  query ($_id: String!) {
    page: sanityPageRentersAffinity(_id: { eq: $_id }) {
      faq {
        ...sanityFAQ
      }
      metadata {
        ...sanityMetadata
      }
      buttonText
      features {
        accordionItems {
          content: _rawContent
          heading
        }
        buttonText
        content: _rawContent
        heading: _rawHeading
        image {
          altText
          img {
            asset {
              gatsbyImageData(placeholder: NONE, width: 1120)
            }
          }
        }
      }
      heading: _rawHeading
      heroBenefits {
        altText
        text
        image {
          asset {
            gatsbyImageData(width: 64, placeholder: NONE)
          }
        }
      }
      heroImage {
        altText
        img {
          asset {
            gatsbyImageData(placeholder: NONE, width: 1120)
          }
        }
      }
      compareTiers {
        ...sanityTiersCompare
      }
      plansHeading: _rawPlansHeading
      subheading
      testimonials {
        altText
        img {
          asset {
            gatsbyImageData(placeholder: NONE, width: 648)
          }
        }
      }
      testimonialsHeading: _rawTestimonialsHeading
      testimonialsSubheading
    }
  }
`;
